import React, { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { FeatureGate } from './FeatureGate';
import { getDocumentAnalytics } from '../lib/analytics';
import { useTranslation } from '../lib/i18n';
import { 
  BarChart3, 
  FileText, 
  Clock, 
  Users, 
  TrendingUp,
  Calendar,
  Download,
  Eye
} from 'lucide-react';

interface AnalyticsData {
  totalDocuments: number;
  documentsThisMonth: number;
  totalWords: number;
  averageWordsPerDocument: number;
  mostActiveDay: string;
  collaborators: number;
  exports: number;
  views: number;
}

export const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Get document statistics
      const { data: documents, error: docsError } = await supabase
        .from('documents')
        .select('id, content, created_at')
        .eq('user_id', user?.id);

      if (docsError) throw docsError;

      // Get collaboration data
      const documentIds = documents?.map(d => d.id) || [];
      const { count: collaborators, error: collabError } = documentIds.length > 0
        ? await supabase
            .from('document_shares')
            .select('*', { count: 'exact', head: true })
            .in('document_id', documentIds)
        : { count: 0, error: null };

      if (collabError) throw collabError;

      // Calculate analytics
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const documentsThisMonth = documents?.filter(doc => 
        new Date(doc.created_at) >= thisMonth
      ).length || 0;

      const totalWords = documents?.reduce((total, doc) => {
        const text = doc.content?.text || '';
        return total + text.split(/\s+/).length;
      }, 0) || 0;

      const averageWordsPerDocument = documents?.length 
        ? Math.round(totalWords / documents.length) 
        : 0;

      // Get most active day (simplified)
      const dayOfWeekKeys = ['analytics.sun', 'analytics.mon', 'analytics.tue', 'analytics.wed', 'analytics.thu', 'analytics.fri', 'analytics.sat'];
      const mostActiveDay = t(dayOfWeekKeys[now.getDay()]);

      const analyticsMetrics = await getDocumentAnalytics(user.id);

      const analyticsData: AnalyticsData = {
        totalDocuments: documents?.length || 0,
        documentsThisMonth,
        totalWords,
        averageWordsPerDocument,
        mostActiveDay,
        collaborators: collaborators || 0,
        exports: analyticsMetrics.totalExports,
        views: analyticsMetrics.totalViews,
      };

      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate feature="analytics">
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-6 h-6 text-blue-500" />
          <h2 className="text-2xl font-bold">{t('analytics.title')}</h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : analytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.totalDocuments')}</p>
                  <p className="text-2xl font-bold">{analytics.totalDocuments}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.thisMonth')}</p>
                  <p className="text-2xl font-bold">{analytics.documentsThisMonth}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.totalWords')}</p>
                  <p className="text-2xl font-bold">{analytics.totalWords.toLocaleString()}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.avgWords')}</p>
                  <p className="text-2xl font-bold">{analytics.averageWordsPerDocument}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-orange-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.collaborators')}</p>
                  <p className="text-2xl font-bold">{analytics.collaborators}</p>
                </div>
                <Users className="w-8 h-8 text-indigo-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.mostActive')}</p>
                  <p className="text-2xl font-bold">{analytics.mostActiveDay}</p>
                </div>
                <Clock className="w-8 h-8 text-pink-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.exports')}</p>
                  <p className="text-2xl font-bold">{analytics.exports}</p>
                </div>
                <Download className="w-8 h-8 text-teal-500" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.views')}</p>
                  <p className="text-2xl font-bold">{analytics.views}</p>
                </div>
                <Eye className="w-8 h-8 text-cyan-500" />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">{t('analytics.noData')}</p>
          </div>
        )}
      </div>
    </FeatureGate>
  );
};